import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { trackingConfig } from "@/config/tracking";

/**
 * Injects GTM and Tawk.to scripts via react-helmet-async.
 *
 * Loading is DEFERRED until first user interaction OR 3 seconds have passed,
 * whichever comes first. This keeps the third-party scripts off the critical
 * render path (previously they started fetching at mount, competing with the
 * homepage bundle + hero image for bandwidth on Slow 4G and hurting LCP).
 *
 * Google Consent Mode v2 defaults are set in index.html BEFORE anything here
 * loads, so consent signalling is unaffected by the defer.
 *
 * Only loads if the respective IDs are configured (non-empty).
 */
const TrackingScripts = () => {
  const { gtmId, tawkPropertyId, tawkWidgetId } = trackingConfig;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // scripts/prerender.mjs sets this flag. Without it, any page slower than
    // the 3s timeout below gets GTM's injected tags (and Tawk) baked into its
    // snapshot, and they then run twice on the live page.
    if (ready || (window as { __TE_PRERENDER__?: boolean }).__TE_PRERENDER__) return;

    const trigger = () => setReady(true);

    // Fire as soon as the user does anything — scroll, click, tap, key press.
    // Ensures trackers load before any engagement event they care about, so we
    // don't miss legitimate interaction signals.
    const events: Array<keyof WindowEventMap> = [
      "scroll",
      "click",
      "keydown",
      "touchstart",
      "mousemove",
    ];
    const opts: AddEventListenerOptions = { once: true, passive: true };
    events.forEach((e) => window.addEventListener(e, trigger, opts));

    // Safety net: if a user reads the page without interacting, still load
    // after 3s so we get a pageview before they bounce.
    const timeout = window.setTimeout(trigger, 3000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, trigger, opts));
      window.clearTimeout(timeout);
    };
  }, [ready]);

  if (!ready) return null;

  return (
    <Helmet>
      {/* Google Tag Manager */}
      {gtmId && (
        <script>{`
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');
        `}</script>
      )}

      {/* Tawk.to Live Chat */}
      {tawkPropertyId && (
        <script>{`
var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();
(function(){
var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
s1.async=true;
s1.src='https://embed.tawk.to/${tawkPropertyId}/${tawkWidgetId}';
s1.charset='UTF-8';
s1.setAttribute('crossorigin','*');
s0.parentNode.insertBefore(s1,s0);
})();
        `}</script>
      )}
    </Helmet>
  );
};

export default TrackingScripts;
